import { request } from "../utils/api";
import { normalizeEmailOptional } from "../utils/email";

const buildQuery = (params = {}) => {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== "",
  );
  if (!entries.length) return "";
  return `?${new URLSearchParams(entries).toString()}`;
};

/** Lowercase email fields on patient create/update payloads (incl. nested). */
function normalizePatientEmailFields(data) {
  if (!data || typeof data !== "object") return data;
  const out = { ...data };
  if (Object.prototype.hasOwnProperty.call(out, "email")) {
    out.email = normalizeEmailOptional(out.email);
  }
  if (Array.isArray(out.physicians)) {
    out.physicians = out.physicians.map((p) => {
      if (!p || typeof p !== "object") return p;
      const row = { ...p };
      if (Object.prototype.hasOwnProperty.call(row, "email")) {
        row.email = normalizeEmailOptional(row.email);
      }
      if (Object.prototype.hasOwnProperty.call(row, "physicianEmail")) {
        row.physicianEmail = normalizeEmailOptional(row.physicianEmail);
      }
      return row;
    });
  }
  if (Array.isArray(out.emergencyContacts)) {
    out.emergencyContacts = out.emergencyContacts.map((c) => {
      if (!c || typeof c !== "object") return c;
      const row = { ...c };
      if (Object.prototype.hasOwnProperty.call(row, "email")) {
        row.email = normalizeEmailOptional(row.email);
      }
      return row;
    });
  }
  return out;
}

export const getPatients = (params) =>
  request(`/patients${buildQuery(params)}`);

/** Tenant-scoped Patient chart tab visibility (enabled keys + labels). */
export const getPatientUiConfig = () => request("/patients/ui-config");

export const getNextPatientMrn = () => request("/patients/next-mrn");

export const getPatientById = (id, params = {}) =>
  request(`/patients/${id}${buildQuery(params)}`);

export const createPatient = (data) =>
  request("/patients", {
    method: "POST",
    body: JSON.stringify(normalizePatientEmailFields(data)),
  });

export const updatePatient = (id, data) =>
  request(`/patients/${id}`, {
    method: "PATCH",
    body: JSON.stringify(normalizePatientEmailFields(data)),
  });

export const updatePatientAuthorizations = (id, data) =>
  request(`/patients/${id}/authorizations`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const deletePatient = (id) =>
  request(`/patients/${id}`, { method: "DELETE" });

export const archivePatient = (id) =>
  request(`/patients/${id}/archive`, { method: "POST" });

export const unarchivePatient = (id) =>
  request(`/patients/${id}/unarchive`, { method: "POST" });

export const updatePatientStatus = (id, status) =>
  request(`/patients/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });

export const getPatientDocuments = (patientId, params = {}) =>
  request(`/patients/${patientId}/documents${buildQuery(params)}`);

export const uploadPatientDocument = (patientId, data) =>
  request(`/patients/${patientId}/documents`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const deletePatientDocument = (patientId, documentId) =>
  request(`/patients/${patientId}/documents/${documentId}`, {
    method: "DELETE",
  });

export const downloadPatientDocument = (patientId, documentId, intent = "download") =>
  request(
    `/patients/${patientId}/documents/${documentId}/download?intent=${intent}`,
  );

export const getPatientAssignments = (patientId, params = {}) =>
  request(`/patients/${patientId}/assignments${buildQuery(params)}`);

export const getAvailableStaff = (patientId, params = {}) =>
  request(
    `/patients/${patientId}/assignments/available${buildQuery(params)}`,
  );

export const assignStaff = (patientId, data) =>
  request(`/patients/${patientId}/assignments`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const replaceStaff = (patientId, data) =>
  request(`/patients/${patientId}/assignments/replace`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const removeStaff = (patientId, assignmentId) =>
  request(`/patients/${patientId}/assignments/${assignmentId}`, {
    method: "DELETE",
  });

export const getStaffSchedule = (patientId, params = {}) =>
  request(`/patients/${patientId}/staff-schedule${buildQuery(params)}`);

export const upsertStaffScheduleSlot = (patientId, data) =>
  request(`/patients/${patientId}/staff-schedule`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const removeStaffScheduleSlot = (patientId, slotId) =>
  request(`/patients/${patientId}/staff-schedule/${slotId}`, {
    method: "DELETE",
  });

export const getPatientNotes = (patientId, params) =>
  request(`/patients/${patientId}/notes${buildQuery(params)}`);

export const createPatientNote = (patientId, data) =>
  request(`/patients/${patientId}/notes`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const deletePatientNote = (patientId, noteId) =>
  request(`/patients/${patientId}/notes/${noteId}`, {
    method: "DELETE",
  });

export const getAssessmentNotes = (patientId, params) =>
  request(`/patients/${patientId}/assessment/notes${buildQuery(params)}`);

export const createAssessmentNote = (patientId, data) =>
  request(`/patients/${patientId}/assessment/notes`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const deleteAssessmentNote = (patientId, noteId) =>
  request(`/patients/${patientId}/assessment/notes/${noteId}`, {
    method: "DELETE",
  });

export const getPatientAssessment = (patientId, params) =>
  request(`/patients/${patientId}/assessment${buildQuery(params)}`);

export const updatePatientAssessment = (patientId, data) =>
  request(`/patients/${patientId}/assessment`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const getPatientOrders = (patientId) =>
  request(`/patients/${patientId}/orders`);

export const getPatientMedications = (patientId, params) =>
  request(`/patients/${patientId}/medications${buildQuery(params)}`);

export const createPatientMedication = (patientId, data) =>
  request(`/patients/${patientId}/medications`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const updatePatientMedication = (patientId, medicationId, data) =>
  request(`/patients/${patientId}/medications/${medicationId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });

export const deletePatientMedication = (patientId, medicationId) =>
  request(`/patients/${patientId}/medications/${medicationId}`, {
    method: "DELETE",
  });

export const getPatientMedicationHistory = (patientId, params) =>
  request(`/patients/${patientId}/medications/history${buildQuery(params)}`);

export const getPatientMedicationPrintData = (patientId, params) =>
  request(`/patients/${patientId}/medications/print${buildQuery(params)}`);

export const getPatientClinicalForms = (patientId, params) =>
  request(`/patients/${patientId}/clinical-forms${buildQuery(params)}`);

export const createPatientClinicalForm = (patientId, data) =>
  request(`/patients/${patientId}/clinical-forms`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const getPatientClinicalForm = (patientId, formId) =>
  request(`/patients/${patientId}/clinical-forms/${formId}`);

export const updatePatientClinicalForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/clinical-forms/${formId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });

export const deletePatientClinicalForm = (patientId, formId) =>
  request(`/patients/${patientId}/clinical-forms/${formId}`, {
    method: "DELETE",
  });

/** Bulk-apply an action mark to one or more clinical forms. */
export const applyClinicalFormMarks = (patientId, data) =>
  request(`/patients/${patientId}/clinical-forms/marks`, {
    method: "POST",
    body: JSON.stringify(data),
  });

/** Bulk print payload: forms (+ optional attachment view URLs) in selection order. */
export const getClinicalFormsBulkPrint = (
  patientId,
  { ids = [], includeAttachments = false } = {},
) => {
  const params = {
    ids: (ids || []).join(","),
    includeAttachments: includeAttachments ? "1" : "0",
  };
  return request(
    `/patients/${patientId}/clinical-forms/bulk-print${buildQuery(params)}`,
  );
};

export const getClinicalFormAttachments = (patientId, formId) =>
  request(`/patients/${patientId}/clinical-forms/${formId}/attachments`);

export const uploadClinicalFormAttachment = (patientId, formId, data) =>
  request(`/patients/${patientId}/clinical-forms/${formId}/attachments`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const updateClinicalFormAttachment = (patientId, attachmentId, data) =>
  request(`/patients/${patientId}/clinical-forms/attachments/${attachmentId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });

export const deleteClinicalFormAttachment = (patientId, attachmentId) =>
  request(`/patients/${patientId}/clinical-forms/attachments/${attachmentId}`, {
    method: "DELETE",
  });

export const downloadClinicalFormAttachment = (
  patientId,
  attachmentId,
  intent = "view",
) =>
  request(
    `/patients/${patientId}/clinical-forms/attachments/${attachmentId}/download?intent=${intent}`,
  );

export const getDailyLogAttachments = (patientId, dailyLogId) =>
  request(`/patients/${patientId}/daily-logs/${dailyLogId}/attachments`);

export const uploadDailyLogAttachment = (patientId, dailyLogId, data) =>
  request(`/patients/${patientId}/daily-logs/${dailyLogId}/attachments`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const updateDailyLogAttachment = (patientId, attachmentId, data) =>
  request(`/patients/${patientId}/daily-logs/attachments/${attachmentId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });

export const deleteDailyLogAttachment = (patientId, attachmentId) =>
  request(`/patients/${patientId}/daily-logs/attachments/${attachmentId}`, {
    method: "DELETE",
  });

export const downloadDailyLogAttachment = (
  patientId,
  attachmentId,
  intent = "view",
) =>
  request(
    `/patients/${patientId}/daily-logs/attachments/${attachmentId}/download?intent=${intent}`,
  );

/* --- Admission documents: Emergency Category & Preparedness --- */

export const getEmergencyCategoryForm = (patientId, formId) =>
  request(
    `/patients/${patientId}/admission-docs/emergency-category/${formId}`,
  );

/** `data.flag` drives the workflow: save | send_to_office | to_be_corrected | completed. */
export const updateEmergencyCategoryForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/admission-docs/emergency-category/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- Admission documents: Patient Consent --- */

export const getPatientConsentForm = (patientId, formId) =>
  request(`/patients/${patientId}/admission-docs/patient-consent/${formId}`);

/** `data.flag` drives the workflow: save | send_to_office | to_be_corrected | completed. */
export const updatePatientConsentForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/admission-docs/patient-consent/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- Admission documents: Notice of Medicare Non-Coverage (NOMNC) --- */

export const getNomncForm = (patientId, formId) =>
  request(`/patients/${patientId}/admission-docs/nomnc/${formId}`);

/** `data.flag` drives the workflow: save | send_to_office | to_be_corrected | completed. */
export const updateNomncForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/admission-docs/nomnc/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- Physician Orders: Physician Order / Supplemental Physician Order --- */

export const getPhysicianOrderForm = (patientId, formId) =>
  request(`/patients/${patientId}/physician-orders/${formId}`);

/** `data.flag` drives the workflow: save | send_to_office | to_be_corrected | completed. */
export const updatePhysicianOrderForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/physician-orders/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- Physician Orders: NOA Order (Notice of Admission) --- */

export const getNoaOrderForm = (patientId, formId) =>
  request(`/patients/${patientId}/physician-orders/noa/${formId}`);

/** `data.flag` drives the workflow: save | send_to_office | to_be_corrected | completed. */
export const updateNoaOrderForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/physician-orders/noa/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
/* --- Physician Orders: Supplemental Physician Order (SPO) --- */

export const getSpoOrderForm = (patientId, formId) =>
  request(`/patients/${patientId}/physician-orders/spo/${formId}`);

export const updateSpoOrderForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/physician-orders/spo/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- Physician Orders: Face-to-Face Encounter --- */

export const getFaceToFaceEncounterForm = (patientId, formId) =>
  request(`/patients/${patientId}/physician-orders/f2f/${formId}`);

export const updateFaceToFaceEncounterForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/physician-orders/f2f/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- Nurse Notes: RN Evaluation Note --- */

export const getRnEvalNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/nurse-notes/rn-eval/${formId}`);

/** `data.flag` drives the workflow: save | send_to_office | to_be_corrected | completed. */
export const updateRnEvalNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/nurse-notes/rn-eval/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- Nurse Notes: RN Visit / Follow Up Note --- */

export const getRnVisitNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/nurse-notes/rn-visit/${formId}`);

/** `data.flag` drives the workflow: save | send_to_office | to_be_corrected | completed. */
export const updateRnVisitNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/nurse-notes/rn-visit/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- Nurse Notes: RN Discharge Visit --- */

export const getRnDischargeNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/nurse-notes/rn-discharge/${formId}`);

/** `data.flag` drives the workflow: save | send_to_office | to_be_corrected | completed. */
export const updateRnDischargeNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/nurse-notes/rn-discharge/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- Nurse Notes: Nurse Missed Visit --- */

export const getRnMissedVisitForm = (patientId, formId) =>
  request(`/patients/${patientId}/nurse-notes/rn-missed/${formId}`);

/** `data.flag` drives the workflow: save | send_to_office | to_be_corrected | completed. */
export const updateRnMissedVisitForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/nurse-notes/rn-missed/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- Nurse Notes: Nurse Supervisory Visit --- */

export const getRnSupVisitForm = (patientId, formId) =>
  request(`/patients/${patientId}/nurse-notes/rn-sup/${formId}`);

/** `data.flag` drives the workflow: save | send_to_office | to_be_corrected | completed. */
export const updateRnSupVisitForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/nurse-notes/rn-sup/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- Nurse Notes: Nurse Re-evaluation Note --- */

export const getRnReevalNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/nurse-notes/rn-reeval/${formId}`);

/** `data.flag` drives the workflow: save | send_to_office | to_be_corrected | completed. */
export const updateRnReevalNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/nurse-notes/rn-reeval/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- Nurse Notes: Nurse Discharge (Non-Visit) --- */

export const getRnDischargeNonVisitForm = (patientId, formId) =>
  request(`/patients/${patientId}/nurse-notes/rn-discharge-non/${formId}`);

/** `data.flag` drives the workflow: save | send_to_office | to_be_corrected | completed. */
export const updateRnDischargeNonVisitForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/nurse-notes/rn-discharge-non/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- PT Notes: Physical Therapy Evaluation Note --- */

export const getPtEvalNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/pt-notes/pt-eval/${formId}`);

export const updatePtEvalNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/pt-notes/pt-eval/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- OT Notes: Occupational Therapy Evaluation Note --- */

export const getOtEvalNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/ot-notes/ot-eval/${formId}`);

export const updateOtEvalNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/ot-notes/ot-eval/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- OT Notes: Occupational Therapy Visit Note --- */

export const getOtVisitNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/ot-notes/ot-visit/${formId}`);

export const updateOtVisitNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/ot-notes/ot-visit/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- OT Notes: Occupational Therapy Supervisory Visit --- */

export const getOtSupVisitForm = (patientId, formId) =>
  request(`/patients/${patientId}/ot-notes/ot-sup/${formId}`);

export const updateOtSupVisitForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/ot-notes/ot-sup/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- OT Notes: Occupational Therapy Re-evaluation Note --- */

export const getOtReevalNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/ot-notes/ot-reeval/${formId}`);

export const updateOtReevalNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/ot-notes/ot-reeval/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- OT Notes: Occupational Therapy Missed Visit --- */

export const getOtMissedVisitForm = (patientId, formId) =>
  request(`/patients/${patientId}/ot-notes/ot-missed/${formId}`);

export const updateOtMissedVisitForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/ot-notes/ot-missed/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- OT Notes: Occupational Therapy Discharge Note --- */

export const getOtDischargeNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/ot-notes/ot-discharge/${formId}`);

export const updateOtDischargeNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/ot-notes/ot-discharge/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- OT Notes: Occupational Therapy Discharge (Non-Visit) --- */

export const getOtDischargeNonVisitForm = (patientId, formId) =>
  request(`/patients/${patientId}/ot-notes/ot-discharge-non/${formId}`);

export const updateOtDischargeNonVisitForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/ot-notes/ot-discharge-non/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- ST Notes: Speech Therapy Evaluation Note --- */

export const getStEvalNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/st-notes/st-eval/${formId}`);

export const updateStEvalNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/st-notes/st-eval/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- ST Notes: Speech Therapy Re-evaluation Note --- */

export const getStReevalNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/st-notes/st-reeval/${formId}`);

export const updateStReevalNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/st-notes/st-reeval/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- ST Notes: Speech Therapy Supervisory Visit --- */

export const getStSupVisitForm = (patientId, formId) =>
  request(`/patients/${patientId}/st-notes/st-sup/${formId}`);

export const updateStSupVisitForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/st-notes/st-sup/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- ST Notes: Speech Therapy Visit Note --- */

export const getStVisitNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/st-notes/st-visit/${formId}`);

export const updateStVisitNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/st-notes/st-visit/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- ST Notes: Speech Therapy Missed Visit --- */

export const getStMissedVisitForm = (patientId, formId) =>
  request(`/patients/${patientId}/st-notes/st-missed/${formId}`);

export const updateStMissedVisitForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/st-notes/st-missed/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- ST Notes: Speech Therapy Discharge Note --- */

export const getStDischargeNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/st-notes/st-discharge/${formId}`);

export const updateStDischargeNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/st-notes/st-discharge/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- ST Notes: Speech Therapy Discharge (Non-Visit) --- */

export const getStDischargeNonVisitForm = (patientId, formId) =>
  request(`/patients/${patientId}/st-notes/st-discharge-nv/${formId}`);

export const updateStDischargeNonVisitForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/st-notes/st-discharge-nv/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const getPtVisitNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/pt-notes/pt-visit/${formId}`);

export const updatePtVisitNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/pt-notes/pt-visit/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const getPtDischargeNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/pt-notes/pt-discharge/${formId}`);

export const updatePtDischargeNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/pt-notes/pt-discharge/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const getPtMissedVisitForm = (patientId, formId) =>
  request(`/patients/${patientId}/pt-notes/pt-missed/${formId}`);

export const updatePtMissedVisitForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/pt-notes/pt-missed/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const getPtSupVisitForm = (patientId, formId) =>
  request(`/patients/${patientId}/pt-notes/pt-sup/${formId}`);

export const updatePtSupVisitForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/pt-notes/pt-sup/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const getPtReevalNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/pt-notes/pt-reeval/${formId}`);

export const updatePtReevalNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/pt-notes/pt-reeval/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const getPtDischargeNonVisitForm = (patientId, formId) =>
  request(`/patients/${patientId}/pt-notes/pt-discharge-non/${formId}`);

export const updatePtDischargeNonVisitForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/pt-notes/pt-discharge-non/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- MSW Notes: Medical Social Work Evaluation Note --- */

export const getMswEvalNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/msw-notes/msw-eval/${formId}`);

export const updateMswEvalNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/msw-notes/msw-eval/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- MSW Notes: Medical Social Work Visit Note --- */

export const getMswVisitNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/msw-notes/msw-visit/${formId}`);

export const updateMswVisitNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/msw-notes/msw-visit/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/* --- MSW Notes: Medical Social Work Missed Visit --- */

export const getMswMissedVisitForm = (patientId, formId) =>
  request(`/patients/${patientId}/msw-notes/msw-missed/${formId}`);

export const updateMswMissedVisitForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/msw-notes/msw-missed/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const getChhaVisitNoteForm = (patientId, formId) =>
  request(`/patients/${patientId}/chha-notes/chha-visit/${formId}`);

export const updateChhaVisitNoteForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/chha-notes/chha-visit/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const getChhaMissedVisitForm = (patientId, formId) =>
  request(`/patients/${patientId}/chha-notes/chha-missed/${formId}`);

export const updateChhaMissedVisitForm = (patientId, formId, data) =>
  request(`/patients/${patientId}/chha-notes/chha-missed/${formId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

/** Who changed what on a form, newest first. Works for any clinical form. */
export const getClinicalFormAuditTrail = (patientId, formId) =>
  request(`/patients/${patientId}/clinical-forms/${formId}/audit`);

/** Logs a print to the audit trail. Works for any clinical form. */
export const logClinicalFormPrint = (patientId, formId) =>
  request(`/patients/${patientId}/clinical-forms/${formId}/print-log`, {
    method: "POST",
  });

export const getPatientCharts = (patientId) =>
  request(`/patients/${patientId}/charts`);

export const getPatientChart = (patientId, chartId) =>
  request(`/patients/${patientId}/charts/${chartId}`);

export const createPatientChart = (patientId, data = {}) =>
  request(`/patients/${patientId}/charts`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const updatePatientChart = (patientId, chartId, data = {}) =>
  request(`/patients/${patientId}/charts/${chartId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });

export const getPatientEpisode = (patientId, chartId, episodeId) =>
  request(`/patients/${patientId}/charts/${chartId}/episodes/${episodeId}`);

export const addPatientEpisode = (patientId, chartId, data = {}) =>
  request(`/patients/${patientId}/charts/${chartId}/episodes`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const updatePatientEpisode = (patientId, chartId, episodeId, data = {}) =>
  request(`/patients/${patientId}/charts/${chartId}/episodes/${episodeId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });

/** Convert File to base64 for API upload */
export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 =
        typeof result === "string" ? result.split(",")[1] || result : "";
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const getScheduledVisits = (patientId, params) =>
  request(
    `/patients/${patientId}/scheduled-visits${buildQuery(params)}`,
  );

export const createScheduledVisit = (patientId, data) =>
  request(`/patients/${patientId}/scheduled-visits`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const updateScheduledVisit = (patientId, visitId, data) =>
  request(`/patients/${patientId}/scheduled-visits/${visitId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });

export const deleteScheduledVisit = (patientId, visitId) =>
  request(`/patients/${patientId}/scheduled-visits/${visitId}`, {
    method: "DELETE",
  });

export const getVisitFrequencies = (patientId, params) =>
  request(
    `/patients/${patientId}/visit-frequencies${buildQuery(params)}`,
  );

export const createVisitFrequency = (patientId, data) =>
  request(`/patients/${patientId}/visit-frequencies`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const updateVisitFrequency = (patientId, planId, data) =>
  request(`/patients/${patientId}/visit-frequencies/${planId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });

export const deleteVisitFrequency = (patientId, planId) =>
  request(`/patients/${patientId}/visit-frequencies/${planId}`, {
    method: "DELETE",
  });
